import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import {
    Clock,
    Filter,
    GitCompareArrows,
    ChevronDown,
    Home,
    CalendarOff,
    TrendingUp,
    TrendingDown,
    Minus,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Building2,
    ChevronRight,
    Table as TableIcon,
    Grid3x3,
    Pencil,
    Check,
    X as XIcon,
    Loader2,
    Search,
    Plus,
    Info,
    Download,
} from "lucide-react";
import { exportAttendanceXlsx } from "@/lib/attendanceExport";
import { MonthMultiSelect } from "@/components/MonthMultiSelect";
import { Skeleton } from "@/components/Skeleton";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import AttendanceHeatmap from "@/components/AttendanceHeatmap";
import EmptyState from "@/components/EmptyState";
import ProvenanceFooter from "@/components/ProvenanceFooter";
import { AttendanceTicker } from "@/components/AttendanceTicker";
import SafeBoundary from "@/components/SafeBoundary";
import { getLateSlabs, getEarlySlabs, shiftMetaOf, shiftKeyOf, editFieldFor, SHIFT_META } from "@/lib/attendanceShifts";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatMonth = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

// FastAPI/Pydantic sometimes returns `detail` as an array of validation
// errors (objects). Passing that raw to <toast> or JSX crashes React with
// "Objects are not valid as a React child". Coerce anything into a safe
// human-readable string.
const errMsg = (e, fallback = "Something went wrong") => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string" && d.trim()) return d;
    if (Array.isArray(d) && d.length) {
        return d.map((x) => (typeof x === "string" ? x : (x?.msg || JSON.stringify(x)))).join("; ");
    }
    if (d && typeof d === "object") {
        return d.msg || JSON.stringify(d);
    }
    return e?.message || fallback;
};

const FIELD_META = [
    { key: "late_slab_1", label: "Late slab 1", group: "Late entry" },
    { key: "late_slab_2", label: "Late slab 2", group: "Late entry" },
    { key: "late_slab_3", label: "After grace (late)", group: "Late entry" },
    { key: "early_slab_1", label: "Early slab 1", group: "Early exit" },
    { key: "early_slab_2", label: "Early slab 2", group: "Early exit" },
    { key: "early_slab_3", label: "Very early exit", group: "Early exit" },
    { key: "total_instances", label: "Total instances", group: "Summary" },
    { key: "oof", label: "OFF / OOF", group: "Summary" },
    { key: "wfh", label: "WFH", group: "Summary" },
];

const SORTABLE_COLUMNS = [
    { key: "employee_name", label: "Employee" },
    { key: "month", label: "Month" },
    { key: "late_slab_1", label: "Late slab 1" },
    { key: "late_slab_2", label: "Late slab 2" },
    { key: "late_slab_3", label: "After grace" },
    { key: "early_slab_1", label: "Early slab 1" },
    { key: "early_slab_2", label: "Early slab 2" },
    { key: "early_slab_3", label: "Very early" },
    { key: "total_instances", label: "Total Instances" },
    { key: "oof", label: "OFF" },
    { key: "wfh", label: "WFH" },
    { key: "present_pct", label: "Present %" },
    { key: "in_office_pct", label: "In-Office %" },
    { key: "critical_instances", label: "Critical Instances" },
];

/* ─── Workday math ──────────────────────────────────────────────────────
 * Counts Mon–Fri in a given YYYY-MM. Public holidays are NOT subtracted
 * automatically — capture them in OFF to exclude.
 *
 * Two metrics are derived per record:
 *   Present %   = (workdays − OFF) / workdays            → "showed up"
 *   In-Office % = (workdays − OFF − WFH) / (workdays − OFF) → "office vs WFH"
 */
const workdaysInMonth = (ym) => {
    if (!ym || typeof ym !== "string" || !ym.includes("-")) return 0;
    const [ys, ms] = ym.split("-");
    const y = parseInt(ys, 10), m = parseInt(ms, 10);
    if (!y || !m) return 0;
    const daysInMonth = new Date(y, m, 0).getDate();
    let n = 0;
    for (let d = 1; d <= daysInMonth; d++) {
        const wd = new Date(y, m - 1, d).getDay(); // 0=Sun,6=Sat
        if (wd !== 0 && wd !== 6) n += 1;
    }
    return n;
};

const HeaderTip = ({ text, testid, children }) => (
    <TooltipProvider delayDuration={150}>
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px] bg-slate-900 text-white text-xs font-normal normal-case tracking-normal leading-relaxed px-3 py-2 shadow-lg" data-testid={testid}>
                {text}
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

const headerInfo = (meta) => {
    const [l1, l2, l3] = meta.lateLabels;
    const [e1, e2, e3] = meta.earlyLabels;
    const [start, end] = meta.window.split("–").map((t) => t.trim());
    return {
        employee_name: "The team member's name. In Flat view, their department is shown underneath.",
        month: "The month these numbers belong to.",
        late: `Days the person clocked in after the ${start} shift start, grouped by how late they were.`,
        late_slab_1: `Days they came in between ${l1}, a little late.`,
        late_slab_2: `Days they came in between ${l2}, noticeably late.`,
        late_slab_3: `Days they came in ${l3.toLowerCase()}, very late. These count as Critical Instances.`,
        early: `Days the person left before the ${end} shift end, grouped by how early they left.`,
        early_slab_1: `Days they left between ${e1}, fairly early.`,
        early_slab_2: `Days they left between ${e2}, a little early.`,
        early_slab_3: `Days they left ${e3.toLowerCase()}, very early. These count as Critical Instances.`,
        oof: "Days taken off (leave) this month. Half days show as 0.5.",
        wfh: "Days worked from home this month.",
        present_pct: "How much of the month the person was working, not on leave. Working days (Mon–Fri, excluding public holidays) minus OFF, divided by working days.",
        in_office_pct: "Of the days the person was working, how many were spent in the office instead of WFH. (Working days − OFF − WFH) ÷ (working days − OFF).",
        critical_instances: `The most serious slips: days they came in ${l3.toLowerCase()} plus days they left ${e3.toLowerCase()}.`,
        total_instances: "Every late entry and early exit this month, across all time windows added together.",
    };
};

const monthsParts = (months) => {
    const sorted = [...months].sort();
    const fm = sorted.map(formatMonth);
    const short = fm.map((m) => m.split(" ")[0]);
    const year = (m) => m.slice(0, 4);
    const sameYear = sorted.every((m) => year(m) === year(sorted[0]));
    const n = sorted.length;
    const main = n > 3
        ? (sameYear ? `${short[0]}–${short[n - 1]}` : `${fm[0]} – ${fm[n - 1]}`)
        : (sameYear ? short.join(", ") : fm.join(", "));
    const sub = `${sameYear ? `${year(sorted[0])} · ` : ""}${n} mo`;
    return { main, sub };
};

const monthsLabel = (months) => {
    const { main, sub } = monthsParts(months);
    return `${main} (${sub})`;
};

const SUM_FIELDS = ["late_slab_1", "late_slab_2", "late_slab_3", "early_slab_1", "early_slab_2", "early_slab_3", "oof", "wfh"];

// One cumulative row per employee + shift across the selected months.
const combineByEmployee = (rows) => {
    const map = new Map();
    for (const r of rows) {
        const key = `${r.user_id}::${shiftKeyOf(r)}`;
        const [l1, l2, l3] = getLateSlabs(r);
        const [e1, e2, e3] = getEarlySlabs(r);
        const vals = [l1, l2, l3, e1, e2, e3, Number(r.oof) || 0, Number(r.wfh) || 0];
        const cur = map.get(key);
        if (!cur) {
            const base = { ...r, id: `combined-${key}`, _combined: true, _months: [r.month], source: undefined };
            SUM_FIELDS.forEach((f, i) => { base[f] = vals[i]; });
            map.set(key, base);
        } else {
            SUM_FIELDS.forEach((f, i) => { cur[f] = Math.round((cur[f] + vals[i]) * 100) / 100; });
            cur._months.push(r.month);
            if (r.month > cur.month) cur.month = r.month;
        }
    }
    return [...map.values()].map((r) => ({ ...r, _monthLabel: monthsLabel(r._months), _monthParts: monthsParts(r._months) }));
};

const monthLabelOf = (r) => r._monthLabel || formatMonth(r.month);

const attendanceStats = (rec) => {
    const workdays = rec?._months ? rec._months.reduce((a, m) => a + workdaysInMonth(m), 0) : workdaysInMonth(rec?.month);
    const oof = Number(rec?.oof) || 0;
    const wfh = Number(rec?.wfh) || 0;
    const presentDays = Math.max(0, workdays - oof);
    const inOffice = Math.max(0, presentDays - wfh);
    const presentPct = workdays > 0 ? Math.round((presentDays / workdays) * 100) : 0;
    const inOfficePct = presentDays > 0 ? Math.round((inOffice / presentDays) * 100) : 0;
    const tone = (p) => (p >= 95 ? "pass" : p >= 80 ? "warn" : "fail");
    return {
        workdays, oof, wfh,
        presentDays, inOffice,
        presentPct, inOfficePct,
        presentTone: tone(presentPct),
        inOfficeTone: tone(inOfficePct),
    };
};

// Kept for backward compat with other callers of inOfficeStats.
const inOfficeStats = (rec) => {
    const s = attendanceStats(rec);
    return { workdays: s.workdays, expected: s.presentDays, oof: s.oof, wfh: s.wfh, inOffice: s.inOffice, pct: s.inOfficePct, tone: s.inOfficeTone };
};

/** Dual-shift-aware header label. Renders the corporate window, the
 * firmware window, or both stacked, depending on `shifts` (a Set of the
 * shift keys currently in view). Colour-coded so the two shifts remain
 * distinguishable when both are shown. */
const ShiftLabel = ({ slot, idx, shifts }) => {
    const showCorp = !shifts || shifts.size === 0 || shifts.has("corporate");
    const showFirm = shifts && shifts.has("firmware");
    const corp = slot === "late"
        ? SHIFT_META.corporate.lateLabels[idx]
        : SHIFT_META.corporate.earlyLabels[idx];
    const firm = slot === "late"
        ? SHIFT_META.firmware.lateLabels[idx]
        : SHIFT_META.firmware.earlyLabels[idx];
    if (showCorp && showFirm) {
        return (
            <span className="inline-flex flex-col items-center leading-tight normal-case tracking-normal" data-testid={`hdr-${slot}-${idx}-both`}>
                <span className="text-slate-600 font-semibold">{corp}</span>
                <span className="text-indigo-600 font-semibold text-[9px]">{firm}</span>
            </span>
        );
    }
    if (showFirm) {
        return <span className="text-indigo-600 normal-case tracking-normal font-semibold" data-testid={`hdr-${slot}-${idx}-firmware`}>{firm}</span>;
    }
    return <span className="normal-case tracking-normal font-semibold" data-testid={`hdr-${slot}-${idx}-corporate`}>{corp}</span>;
};

/** Dedicated table for one shift. Header shows that shift's exact time
 * windows so the labels are unambiguous. Each shift's rows sit in its own
 * table so we never mix a Corporate row under a Firmware column header
 * (or vice versa). */
const CORP = SHIFT_META.corporate;
const FIRM = SHIFT_META.firmware;
const SlabLabel = ({ a, b }) => (
    <span className="inline-flex flex-col items-center leading-tight">
        <span>{a}</span>
        <span className="text-[9px] text-indigo-500 font-medium">{b}</span>
    </span>
);
const ShiftBadge = ({ r }) => {
    const m = shiftMetaOf(r);
    const firm = shiftKeyOf(r) === "firmware";
    return (
        <span title={`${m.label} · ${m.window}`} className={`inline-flex items-center px-1.5 h-4 rounded text-[9px] font-bold tracking-wider border shrink-0 ${firm ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`shift-badge-${r.user_id}-${r.month}`}>
            {m.short}
        </span>
    );
};

const ShiftTable = ({
    groups,
    collapsed,
    toggleCollapse,
    SortHead,
    formatMonth,
    userById,
    canEditCells,
    saveCell,
    setMessageDrill,
    flat = false,
    allRowsSorted,
}) => {
    const shiftKey = "all";
    const hc = headerInfo(CORP);
    const hf = headerInfo(FIRM);
    const both = (k) => `${CORP.short}: ${hc[k]}  ·  ${FIRM.short}: ${hf[k]}`;
    const hi = { ...hc, late: both("late"), early: both("early"), critical_instances: both("critical_instances") };
    ["late_slab_1", "late_slab_2", "late_slab_3", "early_slab_1", "early_slab_2", "early_slab_3"].forEach((k) => { hi[k] = both(k); });
    const total = flat ? (allRowsSorted?.length || 0) : groups.reduce((a, g) => a + g.rows.length, 0);
    return (
        <div data-testid="shift-table-all">
            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3 text-xs text-slate-600">
                    <span className="text-sm font-semibold text-slate-900">All shifts</span>
                    {[CORP, FIRM].map((m, i) => (
                        <span key={m.short} className="inline-flex items-center gap-1.5">
                            <span className={`inline-flex items-center px-1.5 h-4 rounded text-[9px] font-bold tracking-wider border ${i ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{m.short}</span>
                            {m.label} · {m.window}
                        </span>
                    ))}
                </div>
                <div className="text-xs text-slate-500">{total} record{total !== 1 ? "s" : ""}</div>
            </div>
            <table className="w-full min-w-[1280px] table-fixed text-sm border-collapse [&_th]:border-l [&_th]:border-slate-200 [&_td]:border-l [&_td]:border-l-slate-100 [&_thead_tr:first-child>th:first-child]:border-l-0 [&_tbody_td:first-child]:border-l-0" data-testid={`attendance-table-${shiftKey}`}>
                <colgroup>{[13, 7, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 5, 5, 7.5, 7.5, 8, 8].map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}</colgroup>
                <thead className="sticky top-0 z-20 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_0_#e2e8f0]" data-testid="attendance-sticky-head">
                    <tr>
                        <SortHead info={hi.employee_name} colKey="employee_name" label="Employee" rowSpan={2} />
                        <SortHead info={hi.month} nowrap colKey="month" label="Month" rowSpan={2} />
                        <HeaderTip text={hi.late} testid="tip-late-group"><th className="px-3 py-2 text-center cursor-help text-amber-700 shadow-[inset_0_3px_0_0_#f59e0b]" colSpan={3} data-testid="attendance-group-late">Late entry</th></HeaderTip>
                        <HeaderTip text={hi.early} testid="tip-early-group"><th className="px-3 py-2 text-center cursor-help text-rose-700 shadow-[inset_0_3px_0_0_#f43f5e]" colSpan={3} data-testid="attendance-group-early">Early exit</th></HeaderTip>
                        <SortHead info={hi.oof} nowrap colKey="oof" label="OFF" align="right" rowSpan={2} />
                        <SortHead info={hi.wfh} nowrap colKey="wfh" label="WFH" align="right" rowSpan={2} />
                        <SortHead info={hi.present_pct} nowrap colKey="present_pct" label="Present %" align="right" rowSpan={2} />
                        <SortHead info={hi.in_office_pct} nowrap colKey="in_office_pct" label="In-Office %" align="right" rowSpan={2} />
                        <SortHead info={hi.critical_instances} colKey="critical_instances" label="Critical Instances" align="right" rowSpan={2} />
                        <SortHead info={hi.total_instances} colKey="total_instances" label="Total Instances" align="right" rowSpan={2} />
                    </tr>
                    <tr className="text-[10px] font-semibold tracking-wider text-slate-500 normal-case">
                        <SortHead info={hi.late_slab_1} nowrap colKey="late_slab_1" label={<SlabLabel a={CORP.lateLabels[0]} b={FIRM.lateLabels[0]} />} align="center" />
                        <SortHead info={hi.late_slab_2} nowrap colKey="late_slab_2" label={<SlabLabel a={CORP.lateLabels[1]} b={FIRM.lateLabels[1]} />} align="center" />
                        <SortHead info={hi.late_slab_3} nowrap colKey="late_slab_3" label={<SlabLabel a={CORP.lateLabels[2]} b={FIRM.lateLabels[2]} />} align="center" />
                        <SortHead info={hi.early_slab_1} nowrap colKey="early_slab_1" label={<SlabLabel a={CORP.earlyLabels[0]} b={FIRM.earlyLabels[0]} />} align="center" />
                        <SortHead info={hi.early_slab_2} nowrap colKey="early_slab_2" label={<SlabLabel a={CORP.earlyLabels[1]} b={FIRM.earlyLabels[1]} />} align="center" />
                        <SortHead info={hi.early_slab_3} nowrap colKey="early_slab_3" label={<SlabLabel a={CORP.earlyLabels[2]} b={FIRM.earlyLabels[2]} />} align="center" />
                    </tr>
                </thead>
                <tbody>
                    {(() => {
                        // Row renderer shared by Flat and Grouped views.
                        const renderRow = (r) => {
                            const [l1, l2, l3] = getLateSlabs(r);
                            const [e1, e2, e3] = getEarlySlabs(r);
                            const u = userById[r.user_id];
                            const deptLine = flat ? (u?.department || r.department) : null;
                            return (
                                <tr
                                    key={r.id || `${r.user_id}-${r.month}`}
                                    className="hover:bg-slate-50/60 transition-colors border-b border-slate-100"
                                    data-testid={`attendance-row-${r.user_id}-${r.month}`}
                                >
                                    <td className="px-4 py-3 font-medium text-slate-900">
                                        <div className="leading-tight">
                                            <div className="flex items-center gap-1.5"><span className="truncate">{u?.name || r.employee_name}</span><ShiftBadge r={r} /></div>
                                            {deptLine && (
                                                <div className="text-[11px] text-slate-500 font-normal mt-0.5 inline-flex items-center gap-1">
                                                    <Building2 className="h-3 w-3 text-[#25908A]" />
                                                    {deptLine}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        {r._monthParts ? (
                                            <span className="block leading-snug break-words" title={r._monthLabel} data-testid={`attendance-month-${r.user_id}-${r.month}`}>
                                                <span className="block text-sm text-slate-700">{r._monthParts.main}</span>
                                                <span className="block text-[11px] text-slate-400">{r._monthParts.sub}</span>
                                            </span>
                                        ) : (
                                            <span className="text-sm text-slate-700 whitespace-nowrap" data-testid={`attendance-month-${r.user_id}-${r.month}`}>
                                                {formatMonth(r.month)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2 py-3 text-center tabular-nums">
                                        <EditableCell value={l1} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "late_1000_1015", v)} onInfo={r.source === "chat_classifier" ? () => setMessageDrill({ record: r, field: "late_1000_1015" }) : undefined} />
                                    </td>
                                    <td className="px-2 py-3 text-center tabular-nums">
                                        <EditableCell value={l2} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "late_1015_1030", v)} onInfo={r.source === "chat_classifier" ? () => setMessageDrill({ record: r, field: "late_1015_1030" }) : undefined} />
                                    </td>
                                    <td className="px-2 py-3 text-center tabular-nums text-yellow-700 font-medium">
                                        <EditableCell value={l3} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "late_after_1030", v)} tone="warning" onInfo={r.source === "chat_classifier" ? () => setMessageDrill({ record: r, field: "late_after_1030" }) : undefined} />
                                    </td>
                                    <td className="px-2 py-3 text-center tabular-nums">
                                        <EditableCell value={e1} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "early_1800_1830", v)} onInfo={r.source === "chat_classifier" ? () => setMessageDrill({ record: r, field: "early_1800_1830" }) : undefined} />
                                    </td>
                                    <td className="px-2 py-3 text-center tabular-nums">
                                        <EditableCell value={e2} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "early_1830_1900", v)} onInfo={r.source === "chat_classifier" ? () => setMessageDrill({ record: r, field: "early_1830_1900" }) : undefined} />
                                    </td>
                                    <td className="px-2 py-3 text-center tabular-nums text-red-700 font-medium">
                                        <EditableCell value={e3} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "early_before_1800", v)} tone="danger" onInfo={r.source === "chat_classifier" ? () => setMessageDrill({ record: r, field: "early_before_1800" }) : undefined} />
                                    </td>
                                    <td className="px-3 py-3 text-right tabular-nums">
                                        <span className="inline-flex items-center gap-1">
                                            <CalendarOff className="h-3.5 w-3.5 text-amber-500" />
                                            <EditableCell value={r.oof} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "oof", v)} allowDecimal />
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-right tabular-nums">
                                        <span className="inline-flex items-center gap-1">
                                            <Home className="h-3.5 w-3.5 text-blue-500" />
                                            <EditableCell value={r.wfh} canEdit={canEditCells && !r._combined} onSave={(v) => saveCell(r, "wfh", v)} allowDecimal />
                                        </span>
                                    </td>
                                    {(() => {
                                        const ps = attendanceStats(r);
                                        const fmt = (n) => (Number.isInteger(n) ? n : Number(Number(n).toFixed(2)));
                                        const toneCls = (tone) =>
                                            tone === "pass" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : tone === "warn" ? "bg-amber-50 text-amber-700 border-amber-200"
                                            : "bg-rose-50 text-rose-700 border-rose-200";
                                        const presentTip = `Present ${fmt(ps.presentDays)} / ${fmt(ps.workdays)} workdays · OFF ${fmt(ps.oof)} (public holidays excluded — capture them in OFF)`;
                                        const officeTip  = `In-office ${fmt(ps.inOffice)} / ${fmt(ps.presentDays)} present days · WFH ${fmt(ps.wfh)}`;
                                        return (
                                            <>
                                                <td className="px-3 py-3 text-right tabular-nums" title={presentTip} data-testid={`attendance-present-${r.user_id}-${r.month}`}>
                                                    <span className={`inline-flex items-center justify-center min-w-[52px] px-2 py-0.5 rounded-full text-xs font-bold border ${toneCls(ps.presentTone)}`}>
                                                        {ps.presentPct}%
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-right tabular-nums" title={officeTip} data-testid={`attendance-in-office-${r.user_id}-${r.month}`}>
                                                    <span className={`inline-flex items-center justify-center min-w-[52px] px-2 py-0.5 rounded-full text-xs font-bold border ${toneCls(ps.inOfficeTone)}`}>
                                                        {ps.inOfficePct}%
                                                    </span>
                                                </td>
                                            </>
                                        );
                                    })()}
                                    {(() => {
                                        const l3n = Number(l3) || 0;
                                        const e3n = Number(e3) || 0;
                                        const critical = Math.round((l3n + e3n) * 100) / 100;
                                        const fmt = (n) => (Number.isInteger(n) ? n : Number(n.toFixed(2)));
                                        const rm = shiftMetaOf(r);
                                        const tip = `${rm.lateLabels[2]}: ${fmt(l3n)} · ${rm.earlyLabels[2]}: ${fmt(e3n)}`;
                                        const toneCls = critical === 0
                                            ? "bg-slate-50 text-slate-500 border-slate-200"
                                            : critical <= 2
                                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                                : "bg-rose-50 text-rose-700 border-rose-200";
                                        return (
                                            <td className="px-3 py-3 text-right tabular-nums" title={tip} data-testid={`attendance-critical-${r.user_id}-${r.month}`}>
                                                <span className={`inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 rounded-full text-xs font-bold border ${toneCls}`}>
                                                    {fmt(critical)}
                                                </span>
                                            </td>
                                        );
                                    })()}
                                    {(() => {
                                        const lateSum  = (Number(l1) || 0) + (Number(l2) || 0) + (Number(l3) || 0);
                                        const earlySum = (Number(e1) || 0) + (Number(e2) || 0) + (Number(e3) || 0);
                                        const grand    = Math.round((lateSum + earlySum) * 100) / 100;
                                        const fmt = (n) => (Number.isInteger(n) ? n : Number(n.toFixed(2)));
                                        const tip = `Late: ${fmt(lateSum)} · Early: ${fmt(earlySum)}`;
                                        return (
                                            <td className="px-3 py-3 text-right font-bold tabular-nums text-slate-900" title={tip} data-testid={`attendance-total-${r.user_id}-${r.month}`}>
                                                {fmt(grand)}
                                            </td>
                                        );
                                    })()}
                                </tr>
                            );
                        };

                        if (flat) {
                            if (!allRowsSorted || allRowsSorted.length === 0) {
                                return (
                                    <tr><td colSpan={14} className="px-4 py-6 text-center text-slate-500 text-sm">No records.</td></tr>
                                );
                            }
                            return allRowsSorted.map(renderRow);
                        }

                        return groups.map(({ dept, rows }) => {
                            const deptKey = `${shiftKey}::${dept}`;
                            const isCollapsed = !!collapsed[deptKey];
                            return (
                                <React.Fragment key={deptKey}>
                                    <tr
                                        onClick={() => toggleCollapse(deptKey)}
                                        className="bg-teal-50/40 hover:bg-teal-50 cursor-pointer"
                                        data-testid={`dept-group-${shiftKey}-${dept}`}
                                    >
                                        <td colSpan={14} className="px-4 py-2 border-t border-b border-teal-100">
                                            <div className="flex items-center gap-2">
                                                <ChevronRight className={`h-4 w-4 text-[#25908A] transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                                                <Building2 className="h-4 w-4 text-[#25908A]" />
                                                <span className="font-semibold text-slate-900">{dept}</span>
                                                <span className="text-xs text-slate-500">· {rows.length} record{rows.length !== 1 ? "s" : ""}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    {!isCollapsed && rows.map(renderRow)}
                                </React.Fragment>
                            );
                        });
                    })()}
                </tbody>
            </table>
        </div>
    );
};

/* ─── System note card (dark, teal-accented) ────────────────────────────
 * Explains the end-of-month WFH/OOF email cadence so trainees understand
 * late entries vs current-cycle adjustments. Collapsible + dismissible per
 * browser (localStorage). Matches the dark cockpit aesthetic.
 */
const AttendanceSystemNote = () => {
    const [dismissed, setDismissed] = useState(() => {
        try { return window.localStorage.getItem("odyssey.attendance.systemnote.dismissed") === "1"; } catch { return false; }
    });
    if (dismissed) return null;
    const dismiss = () => {
        setDismissed(true);
        try { window.localStorage.setItem("odyssey.attendance.systemnote.dismissed", "1"); } catch {}
    };
    return (
        <div
            className="relative overflow-hidden rounded-2xl text-slate-200"
            style={{ backgroundImage: "linear-gradient(135deg, #0A1627 0%, #0E1E2E 60%, #0B2320 100%)", boxShadow: "0 12px 36px rgba(10, 25, 20, 0.28), inset 0 1px 0 rgba(255,255,255,0.04)", border: "1px solid rgba(46,180,172,0.28)" }}
            data-testid="attendance-system-note"
        >
            {/* Soft radial glow */}
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(46,180,172,0.25), transparent)" }} />
            <div className="relative px-6 py-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.26em] text-teal-300">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(46,180,172,0.9)]" />
                            System note · Attendance
                        </div>
                        <div className="mt-3 text-[14px] leading-relaxed text-slate-100">
                            <strong className="text-white font-semibold">WFH &amp; OOF entries are updated based on information received by email at the end of each month.</strong>
                            <span className="text-slate-300"> Currently, leave and WFH records are updated through the </span>
                            <span className="text-teal-300 font-semibold">26th</span>
                            <span className="text-slate-300">.</span>
                        </div>
                        <div className="mt-2 text-[13.5px] leading-relaxed text-slate-300">
                            Any leave or WFH reported <em className="not-italic text-slate-200">after the 26th</em> — including adjustments related to the previous month — will be reflected in the <strong className="text-white font-semibold">current month&rsquo;s attendance cycle</strong>.
                        </div>

                        {/* Nested example callout */}
                        <div
                            className="mt-4 rounded-xl px-4 py-3"
                            style={{ border: "1px solid rgba(46,180,172,0.26)", background: "rgba(46,180,172,0.04)" }}
                        >
                            <div className="text-[10px] font-bold uppercase tracking-[0.26em] text-teal-300">Example</div>
                            <div className="mt-1.5 text-[13px] leading-relaxed text-slate-200">
                                A leave taken on <strong className="text-white">28<sup>th</sup> July</strong> but reported after the July attendance was closed will be adjusted in <strong className="text-white">August&rsquo;s attendance</strong>.
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={dismiss}
                        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-100 hover:bg-white/5 transition"
                        title="Dismiss"
                        data-testid="attendance-system-note-close"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

const Attendance = () => {
    const { user } = useAuth();
    const { users, depts } = useData();

    const [records, setRecords] = useState([]);
    const [months, setMonths] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);

    const [monthFilter, setMonthFilter] = useState([]);
    const [userFilter, setUserFilter] = useState("all");
    const [deptFilter, setDeptFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [showAddDialog, setShowAddDialog] = useState(false);
    // Drill-down modal for the chat-classifier message trail on a cell.
    // `record` is the attendance row; `field` is the slab column that was clicked.
    const [messageDrill, setMessageDrill] = useState(null);
    const [compareMode, setCompareMode] = useState(false);
    const [cmpUser, setCmpUser] = useState("");
    const [cmpMonthA, setCmpMonthA] = useState("");
    const [cmpMonthB, setCmpMonthB] = useState("");
    const [compareResult, setCompareResult] = useState(null);
    const [comparing, setComparing] = useState(false);

    const [sortKey, setSortKey] = useState("month");
    const [sortDir, setSortDir] = useState("desc");
    const [collapsed, setCollapsed] = useState({});
    const [collapseInitialized, setCollapseInitialized] = useState(false);

    // Analytics panel visibility — off by default. Toggled from
    // Control Panel → Attendance. Persisted in localStorage; same-tab
    // updates are received via a custom "odyssey:attendance-insights" event.
    const [insightsEnabled, setInsightsEnabled] = useState(() => {
        try { return window.localStorage.getItem("odyssey.attendance.insights") === "1"; } catch { return false; }
    });
    useEffect(() => {
        const sync = () => {
            try { setInsightsEnabled(window.localStorage.getItem("odyssey.attendance.insights") === "1"); } catch {}
        };
        window.addEventListener("storage", sync);
        window.addEventListener("odyssey:attendance-insights", sync);
        return () => {
            window.removeEventListener("storage", sync);
            window.removeEventListener("odyssey:attendance-insights", sync);
        };
    }, []);

    // Grouped-by-department vs. flat view. Flat is super_admin-only — a
    // single sortable table across every employee.
    const [viewMode, setViewMode] = useState(() => {
        try { return window.localStorage.getItem("odyssey.attendance.view_mode") === "flat" ? "flat" : "grouped"; } catch { return "grouped"; }
    });
    const toggleViewMode = (next) => {
        setViewMode(next);
        try { window.localStorage.setItem("odyssey.attendance.view_mode", next); } catch {}
    };
    const [view, setView] = useState("table"); // "table" | "heatmap"
    const canEditCells = user?.role === "super_admin";

    // Inline cell edit — optimistic with rollback on failure. The backend
    // also recomputes `total_instances` whenever a late/early bucket
    // changes so we patch that field locally too once the response lands.
    // The `field` passed in is a legacy corporate name; for firmware rows
    // we remap it to the shift-agnostic `late_slab_1..3` / `early_slab_*`
    // fields via `editFieldFor` so we don't accidentally write to the
    // wrong (invisible) column.
    const saveCell = async (record, legacyField, value) => {
        const recordId = record.id;
        const field = editFieldFor(record, legacyField);
        const prev = records;
        // Optimistic merge — adjust total_instances locally for snappy UI
        setRecords((rows) =>
            rows.map((r) => {
                if (r.id !== recordId) return r;
                const updated = { ...r, [field]: value };
                const legacyKeys = [
                    "late_1000_1015", "late_1015_1030", "late_after_1030",
                    "early_1800_1830", "early_1830_1900", "early_before_1800",
                ];
                const slabKeys = [
                    "late_slab_1", "late_slab_2", "late_slab_3",
                    "early_slab_1", "early_slab_2", "early_slab_3",
                ];
                if (legacyKeys.includes(field) || slabKeys.includes(field)) {
                    const lates = getLateSlabs(updated);
                    const earlies = getEarlySlabs(updated);
                    updated.total_instances = lates.concat(earlies).reduce((a, b) => a + (Number(b) || 0), 0);
                }
                return updated;
            })
        );
        try {
            const resp = await api.patch(`/attendance/${recordId}`, { field, value });
            // Reconcile with server truth
            setRecords((rows) => rows.map((r) => (r.id === recordId ? { ...r, ...resp.data } : r)));
            toast.success("Saved");
        } catch (e) {
            setRecords(prev);
            toast.error(errMsg(e, "Could not save"));
        }
    };

    const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u])), [users]);
    const deptById = useMemo(() => Object.fromEntries((depts || []).map((d) => [d.id, d])), [depts]);

    // Visible-users follows the org chart's manager_id chain, not the role
    // string. This matches the backend's `_scope_user_ids`: any user with
    // direct reports automatically sees their whole subtree (so a "Member"
    // who has people under them still has visibility). Super admins see
    // everyone.
    const visibleUsers = useMemo(() => {
        if (!user || !users?.length) return [];
        if (user.role === "super_admin") return users;
        // BFS down the manager_id chain
        const visible = new Set([user.user_id]);
        let frontier = [user.user_id];
        while (frontier.length) {
            const next = [];
            for (const mid of frontier) {
                for (const u of users) {
                    if (u.manager_id === mid && !visible.has(u.user_id)) {
                        visible.add(u.user_id);
                        next.push(u.user_id);
                    }
                }
            }
            frontier = next;
        }
        return users.filter((u) => visible.has(u.user_id));
    }, [user, users]);

    const load = async () => {
        setLoading(true);
        try {
            const [r, m, me] = await Promise.all([
                api.get("/attendance"),
                api.get("/attendance/months"),
                api.get("/attendance/meta").catch(() => ({ data: null })),
            ]);
            setRecords(r.data || []);
            setMonths(m.data?.months || []);
            setMeta(me.data);
        } catch {
            toast.error("Failed to load attendance");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        if (user?.role === "employee") setUserFilter(user.user_id);
    }, [user]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return records.filter((r) => {
            if (monthFilter.length && !monthFilter.includes(r.month)) return false;
            if (userFilter !== "all" && r.user_id !== userFilter) return false;
            if (deptFilter !== "all" && r.department_id !== deptFilter && r.department_name !== deptById[deptFilter]?.name) return false;
            if (q) {
                const hay = `${r.employee_name || ""} ${r.employee_email || ""} ${r.department_name || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [records, monthFilter, userFilter, deptFilter, search, deptById]);

    const displayRows = useMemo(
        () => (monthFilter.length >= 2 ? combineByEmployee(filtered) : filtered),
        [filtered, monthFilter]
    );

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(key === "month" ? "desc" : "asc");
        }
    };

    const sortRows = (rows) => {
        const sign = sortDir === "asc" ? 1 : -1;
        const slabIdx = {
            late_slab_1: ["late", 0], late_slab_2: ["late", 1], late_slab_3: ["late", 2],
            early_slab_1: ["early", 0], early_slab_2: ["early", 1], early_slab_3: ["early", 2],
        };
        const valueOf = (r) => {
            if (slabIdx[sortKey]) {
                const [kind, i] = slabIdx[sortKey];
                return (kind === "late" ? getLateSlabs(r) : getEarlySlabs(r))[i];
            }
            if (sortKey === "in_office_pct") return attendanceStats(r).inOfficePct;
            if (sortKey === "present_pct")   return attendanceStats(r).presentPct;
            if (sortKey === "critical_instances") {
                const [, , l3] = getLateSlabs(r);
                const [, , e3] = getEarlySlabs(r);
                return (Number(l3) || 0) + (Number(e3) || 0);
            }
            return r[sortKey];
        };
        return [...rows].sort((a, b) => {
            const va = valueOf(a);
            const vb = valueOf(b);
            if (typeof va === "string" && typeof vb === "string") return va.localeCompare(vb) * sign;
            return ((Number(va) || 0) - (Number(vb) || 0)) * sign;
        });
    };

    // Split visible records into two shift buckets, then group each
     // bucket by department. Each bucket renders as its own table with
     // shift-specific headers — no cross-shift ambiguity.
    const bucketed = useMemo(() => {
        const m = new Map();
        for (const r of displayRows) {
            const dept = r.department_name || deptById[r.department_id]?.name || "— Unassigned —";
            if (!m.has(dept)) m.set(dept, []);
            m.get(dept).push(r);
        }
        const arr = Array.from(m.entries()).map(([dept, rows]) => ({ dept, rows: sortRows(rows) }));
        arr.sort((a, b) => a.dept.localeCompare(b.dept));
        return arr;
    // eslint-disable-next-line
    }, [displayRows, sortKey, sortDir, deptById]);

    // Flat per-shift sorted lists — used when super admin picks "flat view"
    // and the table ungroups departments into one long sortable list.
    const flatBuckets = useMemo(() => {
        const buckets = { corporate: [], firmware: [] };
        for (const r of displayRows) {
            buckets[shiftKeyOf(r)].push(r);
        }
        return {
            corporate: sortRows(buckets.corporate),
            firmware: sortRows(buckets.firmware),
        };
    // eslint-disable-next-line
    }, [displayRows, sortKey, sortDir]);
    // eslint-disable-next-line
    const flatAll = useMemo(() => sortRows(displayRows), [displayRows, sortKey, sortDir]);

    // Combined flat list of dept groups from both shift buckets (used by
    // collapse/expand controls). Dept keys are namespaced by shift so
    // "Engineering" in Corporate ≠ "Engineering" in Firmware.
    const allDeptKeys = useMemo(() => {
        const keys = [];
        for (const g of bucketed) keys.push(`all::${g.dept}`);
        return keys;
    }, [bucketed]);

    // Collapse all departments by default once we have records
    useEffect(() => {
        if (collapseInitialized || allDeptKeys.length === 0) return;
        const initial = {};
        for (const k of allDeptKeys) initial[k] = true;
        setCollapsed(initial);
        setCollapseInitialized(true);
    }, [allDeptKeys, collapseInitialized]);

    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        setExporting(true);
        try {
            const monthsPart = monthFilter.length ? [...monthFilter].sort().join("_") : "all-months";
            const ok = await exportAttendanceXlsx({
                buckets: flatBuckets,
                deptName: (r) => r.department_name || deptById[r.department_id]?.name || "Unassigned",
                stats: attendanceStats,
                monthLabel: monthLabelOf,
                fileName: `attendance_${monthsPart}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            });
            if (ok) toast.success("Attendance exported");
            else toast.error("No records match the current filters");
        } catch (e) {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const toggleCollapse = (deptKey) => setCollapsed((c) => ({ ...c, [deptKey]: !c[deptKey] }));
    const expandAll = () => {
        const next = {};
        for (const k of allDeptKeys) next[k] = false;
        setCollapsed(next);
    };
    const collapseAll = () => {
        const next = {};
        for (const k of allDeptKeys) next[k] = true;
        setCollapsed(next);
    };

    const runCompare = async () => {
        if (!cmpUser || !cmpMonthA || !cmpMonthB) {
            toast.error("Pick employee and both months");
            return;
        }
        if (cmpMonthA === cmpMonthB) {
            toast.error("Pick two different months");
            return;
        }
        setComparing(true);
        try {
            const r = await api.get(`/attendance/compare?user_id=${cmpUser}&month_a=${cmpMonthA}&month_b=${cmpMonthB}`);
            setCompareResult(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Compare failed"));
        } finally {
            setComparing(false);
        }
    };

    const SortHead = ({ colKey, label, align = "left", colSpan, rowSpan, className = "", info, nowrap }) => {
        const active = sortKey === colKey;
        const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
        return (
            <HeaderTip text={info} testid={`tip-${colKey}`}>
                <th
                    onClick={() => toggleSort(colKey)}
                    colSpan={colSpan}
                    rowSpan={rowSpan}
                    className={`px-2 py-3 cursor-pointer select-none hover:bg-slate-100 transition-colors text-center ${className}`}
                    data-testid={`sort-${colKey}`}
                >
                    <span className={`inline-flex items-center justify-center gap-1 leading-tight ${nowrap ? "whitespace-nowrap" : ""} ${active ? "text-[#25908A]" : ""}`}>
                        {label}
                        <Icon className="h-3 w-3 shrink-0" />
                    </span>
                </th>
            </HeaderTip>
        );
    };

    return (
        <div className="space-y-8" data-testid="attendance-page">
            {/* Moving ticker — communicates the monthly data-refresh SLA so users
                aren't surprised when this month's numbers look incomplete. */}
            <AttendanceTicker />

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Monthly rollup</div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-1">
                        Attendance
                    </h1>
                    <p className="text-slate-500 mt-2">Late entries, early exits, OFF days and WFH — grouped by department, sorted by you.</p>
                </div>
            </div>

            <AttendanceSystemNote />


            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <Filter className="h-4 w-4 text-slate-400" />
                        Filters
                    </div>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search employee or department…"
                            data-testid="attendance-search-input"
                            className="h-10 pl-8 pr-3 w-64 rounded-lg border border-slate-200 text-sm bg-white focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/30 outline-none transition-colors"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                aria-label="Clear search"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700"
                            >
                                <XIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    <MonthMultiSelect value={monthFilter} onChange={setMonthFilter} months={months} format={formatMonth} />
                    {user?.role !== "employee" && (
                        <Select
                            value={deptFilter}
                            onChange={(v) => { setDeptFilter(v); setUserFilter("all"); }}
                            options={[{ value: "all", label: "All departments" }, ...[...(depts || [])].sort((a, b) => a.name.localeCompare(b.name)).map((d) => ({ value: d.id, label: d.name }))]}
                            testid="attendance-dept-filter"
                        />
                    )}
                    {user?.role !== "employee" && (
                        <Select
                            value={userFilter}
                            onChange={setUserFilter}
                            options={[{ value: "all", label: "All team members" }, ...visibleUsers.filter((u) => deptFilter === "all" || u.department_id === deptFilter).map((u) => ({ value: u.user_id, label: u.name }))]}
                            testid="attendance-user-filter"
                        />
                    )}
                    <button
                        onClick={() => setCompareMode((v) => !v)}
                        data-testid="toggle-compare-mode-btn"
                        className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg border font-medium text-sm transition-colors ${
                            compareMode
                                ? "border-[#2EB4AC] bg-teal-50 text-[#25908A]"
                                : "border-slate-200 bg-white text-slate-700 hover:border-[#2EB4AC] hover:text-[#2EB4AC]"
                        }`}
                    >
                        <GitCompareArrows className="h-4 w-4" />
                        {compareMode ? "Comparing two months" : "Compare two months"}
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        data-testid="attendance-export-btn"
                        title="Download the table as Excel, with your current filters and sort"
                        className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium text-sm hover:border-[#2EB4AC] hover:text-[#2EB4AC] transition-colors disabled:opacity-60"
                    >
                        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Export
                    </button>
                    {canEditCells && (
                        <button
                            onClick={() => setShowAddDialog(true)}
                            data-testid="add-attendance-row-btn"
                            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-medium text-sm transition-colors"
                        >
                            <Plus className="h-4 w-4" /> Add row
                        </button>
                    )}

                    {/* View-mode toggle */}
                    <div className="ml-auto inline-flex items-center h-10 bg-slate-50 rounded-lg p-1 border border-slate-200" role="tablist" aria-label="View mode">
                        <button
                            onClick={() => setView("table")}
                            aria-pressed={view === "table"}
                            data-testid="attendance-view-table"
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                view === "table" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                            }`}
                        >
                            <TableIcon className="h-3.5 w-3.5" /> Table
                        </button>
                        <button
                            onClick={() => setView("heatmap")}
                            aria-pressed={view === "heatmap"}
                            data-testid="attendance-view-heatmap"
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                view === "heatmap" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                            }`}
                        >
                            <Grid3x3 className="h-3.5 w-3.5" /> Heatmap
                        </button>
                    </div>
                </div>
            </div>

            {compareMode && (
                <div className="bg-white border-2 border-dashed border-teal-300 rounded-lg p-6 bg-teal-50/30">
                    <div className="flex items-center gap-2 mb-4">
                        <GitCompareArrows className="h-4 w-4 text-[#25908A]" />
                        <h3 className="font-heading text-lg font-semibold text-slate-900">Compare two months</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <Select
                            value={cmpUser}
                            onChange={setCmpUser}
                            options={[{ value: "", label: "Select employee" }, ...visibleUsers.map((u) => ({ value: u.user_id, label: u.name }))]}
                            testid="compare-user-select"
                        />
                        <Select
                            value={cmpMonthA}
                            onChange={setCmpMonthA}
                            options={[{ value: "", label: "Month A" }, ...months.map((m) => ({ value: m, label: formatMonth(m) }))]}
                            testid="compare-month-a-select"
                        />
                        <Select
                            value={cmpMonthB}
                            onChange={setCmpMonthB}
                            options={[{ value: "", label: "Month B" }, ...months.map((m) => ({ value: m, label: formatMonth(m) }))]}
                            testid="compare-month-b-select"
                        />
                        <button
                            onClick={runCompare}
                            disabled={comparing}
                            data-testid="run-compare-btn"
                            className="h-10 px-4 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                            {comparing ? "Comparing…" : "Compare"}
                        </button>
                    </div>

                    {compareResult && (
                        <div className="mt-6">
                            <CompareTable result={compareResult} cmpUser={cmpUser} userById={userById} />
                        </div>
                    )}
                </div>
            )}

            {/* Grouped, sortable, sticky table OR Heatmap */}
            {view === "heatmap" ? (
                filtered.length === 0 ? (
                    <EmptyState
                        icon={Clock}
                        title="No attendance records to map"
                        description="Upload a monthly rollup from Control Panel and the heatmap will light up with severity patterns across your team."
                        testid="heatmap-empty-state"
                    />
                ) : (
                    <AttendanceHeatmap
                        records={filtered}
                        months={[...months].sort()}
                        onCellClick={(r) => {
                            setView("table");
                            setMonthFilter([r.month]);
                            setUserFilter(r.user_id);
                        }}
                    />
                )
            ) : (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                        <h3 className="font-heading text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                            <Clock className="h-4 w-4 text-[#2EB4AC]" />
                            Monthly records
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-1" data-testid="attendance-present-disclaimer">
                            Present % = (workdays − OFF) ÷ workdays · In-Office % = (workdays − OFF − WFH) ÷ (workdays − OFF). Mon–Fri basis; public holidays excluded.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {user?.role === "super_admin" && (
                            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-xs" data-testid="attendance-view-toggle">
                                <button
                                    type="button"
                                    onClick={() => toggleViewMode("grouped")}
                                    className={`px-2.5 h-7 rounded font-semibold transition-colors ${viewMode === "grouped" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
                                    data-testid="attendance-view-grouped"
                                    title="Group rows by department"
                                >
                                    Grouped
                                </button>
                                <button
                                    type="button"
                                    onClick={() => toggleViewMode("flat")}
                                    className={`px-2.5 h-7 rounded font-semibold transition-colors ${viewMode === "flat" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
                                    data-testid="attendance-view-flat"
                                    title="Flat sortable list across every employee"
                                >
                                    Flat
                                </button>
                            </div>
                        )}
                        {viewMode === "grouped" && allDeptKeys.length > 1 && (
                            <div className="inline-flex items-center gap-1 text-xs">
                                <button
                                    onClick={expandAll}
                                    data-testid="expand-all-btn"
                                    className="px-2 h-7 rounded-md text-slate-500 hover:text-[#25908A] hover:bg-slate-100 font-medium transition-colors"
                                >
                                    Expand all
                                </button>
                                <span className="text-slate-300">·</span>
                                <button
                                    onClick={collapseAll}
                                    data-testid="collapse-all-btn"
                                    className="px-2 h-7 rounded-md text-slate-500 hover:text-[#25908A] hover:bg-slate-100 font-medium transition-colors"
                                >
                                    Collapse all
                                </button>
                            </div>
                        )}
                        {/* Shift lens removed — the table itself splits into two shift-scoped sub-tables below. */}
                        <div className="text-xs text-slate-500" data-testid="attendance-record-count">{displayRows.length} {monthFilter.length >= 2 ? "combined " : ""}record{displayRows.length !== 1 ? "s" : ""}</div>
                    </div>
                </div>

                {loading ? (
                    <div className="p-5 space-y-3">
                        {Array.from({ length: 3 }).map((_, g) => (
                            <div key={g}>
                                <Skeleton className="h-6 w-56 mb-3" />
                                {Array.from({ length: 4 }).map((_, r) => (
                                    <div key={r} className="flex items-center gap-4 py-2.5">
                                        <Skeleton className="h-3 w-32" />
                                        <Skeleton className="h-5 w-20" />
                                        <div className="flex gap-3 ml-auto">
                                            {Array.from({ length: 6 }).map((_, c) => <Skeleton key={c} className="h-3 w-8" />)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : displayRows.length === 0 ? (
                    <div className="p-10">
                        <EmptyState
                            icon={Clock}
                            title="No attendance records yet"
                            description={user?.role === "employee"
                                ? "Your manager hasn't uploaded attendance for this period yet. Check back after the monthly rollup is complete."
                                : "Monthly rollups uploaded from the Control Panel will appear here. Start by downloading the sample template, filling it in, and uploading it back."}
                            primary={user?.role !== "employee" ? {
                                label: "Go to Control Panel",
                                onClick: () => window.location.assign("/control-panel?tab=attendance"),
                            } : undefined}
                            testid="attendance-empty-state"
                        />
                    </div>
                ) : (
                    bucketed.length === 0 ? null : (
                        <div className="max-h-[70vh] overflow-auto scrollbar-thin" data-testid="attendance-scroll-area">
                            <ShiftTable
                                groups={bucketed}
                                collapsed={collapsed}
                                toggleCollapse={toggleCollapse}
                                SortHead={SortHead}
                                formatMonth={formatMonth}
                                userById={userById}
                                canEditCells={canEditCells}
                                saveCell={saveCell}
                                setMessageDrill={setMessageDrill}
                                flat={viewMode === "flat"}
                                allRowsSorted={flatAll}
                            />
                        </div>
                    )
                )}
                {!loading && filtered.length > 0 && (user?.role !== "employee") && (
                    <div className="px-5 pb-4">
                        <ProvenanceFooter meta={meta} filteredCount={filtered.length} />
                    </div>
                )}
            </div>
            )}

            {showAddDialog && (
                <AddAttendanceDialog
                    visibleUsers={users || []}
                    onClose={() => setShowAddDialog(false)}
                    onCreated={async () => {
                        setShowAddDialog(false);
                        try {
                            const r = await api.get("/attendance");
                            setRecords(r.data || []);
                            toast.success("Attendance row added");
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.error("Reload after add failed:", e);
                            toast.success("Attendance row added — reload to see it.");
                        }
                    }}
                />
            )}
            {messageDrill && (
                <AttendanceMessagesModal
                    record={messageDrill.record}
                    field={messageDrill.field}
                    onClose={() => setMessageDrill(null)}
                />
            )}
        </div>
    );
};

// ---------- Moving notice ticker ----------
// Extracted to /app/frontend/src/components/AttendanceTicker.jsx so the
// Dashboard can render the same live notice.

const Select = ({ value, onChange, options, testid }) => (
    <div className="relative">
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="appearance-none h-10 pl-3 pr-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:border-[#2EB4AC] focus:ring-2 focus:ring-teal-100 transition-colors"
        >
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
    </div>
);

const CompareTable = ({ result, cmpUser, userById }) => {
    const { month_a, month_b, deltas } = result;
    const name = userById[cmpUser]?.name || month_a?.employee_name || month_b?.employee_name || "—";
    return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="compare-result-panel">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="font-medium text-slate-900">{name}</div>
                <div className="text-xs text-slate-500">
                    {formatMonth(month_a?.month)} → {formatMonth(month_b?.month)}
                </div>
            </div>
            {!month_a && !month_b ? (
                <div className="p-8 text-center text-slate-500 text-sm">No records for either month</div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-4 py-2">Metric</th>
                            <th className="px-4 py-2 text-right">{formatMonth(month_a?.month) || "Month A"}</th>
                            <th className="px-4 py-2 text-right">{formatMonth(month_b?.month) || "Month B"}</th>
                            <th className="px-4 py-2 text-right">Change</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {FIELD_META.map((f) => {
                            const a = month_a?.[f.key] || 0;
                            const b = month_b?.[f.key] || 0;
                            const d = deltas?.[f.key] || 0;
                            const good = d < 0;
                            const bad = d > 0 && f.key !== "wfh";
                            const Icon = d === 0 ? Minus : d > 0 ? TrendingUp : TrendingDown;
                            const color = d === 0 ? "text-slate-400" : good ? "text-green-600" : bad ? "text-red-600" : "text-blue-600";
                            return (
                                <tr key={f.key} className={f.key === "total_instances" ? "bg-slate-50/50 font-semibold" : ""}>
                                    <td className="px-4 py-2">
                                        <div className="text-slate-700">{f.label}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-400">{f.group}</div>
                                    </td>
                                    <td className="px-4 py-2 text-right tabular-nums">{a}</td>
                                    <td className="px-4 py-2 text-right tabular-nums">{b}</td>
                                    <td className="px-4 py-2 text-right">
                                        <span className={`inline-flex items-center gap-1 ${color} tabular-nums font-medium`}>
                                            <Icon className="h-3.5 w-3.5" />
                                            {d === 0 ? "—" : (d > 0 ? `+${d}` : d)}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

// ---------- Inline editable numeric cell ----------
// When canEdit, the cell renders as a button. Click → tiny inline input
// with confirm / cancel + Enter/Esc keyboard handling. Disabled cells fall
// back to the plain number display so the rest of the table looks the same
// to non-admin viewers.
const EditableCell = ({ value, canEdit, onSave, tone, onInfo, allowDecimal = false }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value || 0));
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing) {
            setDraft(String(value || 0));
            // Defer focus so the input is mounted
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
        }
    }, [editing, value]);

    const infoBtn = onInfo && value > 0 ? (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onInfo(); }}
            title="Show the chat messages that produced this number"
            data-testid="attendance-cell-drilldown"
            className="ml-0.5 opacity-40 hover:opacity-100 text-[#25908A] transition-opacity"
        >
            <Info className="h-3 w-3" />
        </button>
    ) : null;

    if (!canEdit) {
        return <span className="inline-flex items-center">{value || 0}{infoBtn}</span>;
    }

    if (!editing) {
        return (
            <span className="inline-flex items-center">
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    title="Click to edit"
                    className={`group inline-flex items-center gap-1 px-1.5 py-0.5 -mx-1.5 rounded hover:bg-teal-50 focus:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]/40 transition-colors ${
                        tone === "warning" ? "text-yellow-700" : tone === "danger" ? "text-red-700" : "text-slate-900"
                    }`}
                >
                    <span className="tabular-nums">{value || 0}</span>
                    <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 text-slate-400 transition-opacity" />
                </button>
                {infoBtn}
            </span>
        );
    }

    const commit = async () => {
        // OOF / WFH accept half-days (0.5). Late/early buckets are event
        // counts so we still coerce to int to keep totals clean.
        const parsed = allowDecimal ? parseFloat(draft) : parseInt(draft, 10);
        const safe = Number.isFinite(parsed) ? parsed : 0;
        const v = Math.max(0, Math.min(999, safe));
        if (v === (value || 0)) {
            setEditing(false);
            return;
        }
        setSaving(true);
        await onSave(v);
        setSaving(false);
        setEditing(false);
    };

    return (
        <span className="inline-flex items-center gap-0.5 align-middle">
            <input
                ref={inputRef}
                type="number"
                min={0}
                max={999}
                step={allowDecimal ? "0.5" : "1"}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
                }}
                disabled={saving}
                className="w-12 h-7 px-1 text-sm text-center border border-[#2EB4AC] rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]/40 tabular-nums"
            />
            <button
                type="button"
                onClick={commit}
                disabled={saving}
                aria-label="Save"
                className="h-6 w-6 inline-flex items-center justify-center rounded text-emerald-600 hover:bg-emerald-50"
            >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </button>
            <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={saving}
                aria-label="Cancel"
                className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100"
            >
                <XIcon className="h-3.5 w-3.5" />
            </button>
        </span>
    );
};

/** Portal modal — renders the archived chat-classifier trail for one
 * (record × field). Fetches lazily so the initial Attendance table stays
 * lean; caches nothing (data is cheap to re-fetch). */
const FIELD_LABELS = {
    late_1000_1015: { corporate: "10:00 – 10:15 lates", firmware: "09:00 – 09:15 lates" },
    late_1015_1030: { corporate: "10:15 – 10:30 lates", firmware: "09:15 – 09:30 lates" },
    late_after_1030: { corporate: "After 10:30 lates", firmware: "After 09:30 lates" },
    early_1800_1830: { corporate: "18:00 – 18:30 early exits", firmware: "17:00 – 17:30 early exits" },
    early_1830_1900: { corporate: "18:30 – 19:00 early exits", firmware: "17:30 – 18:00 early exits" },
    early_before_1800: { corporate: "Before 18:00 early exits", firmware: "Before 17:00 early exits" },
};

const labelForField = (field, record) => {
    const entry = FIELD_LABELS[field];
    if (!entry) return field;
    return entry[shiftKeyOf(record)] || entry.corporate;
};

const AttendanceMessagesModal = ({ record, field, onClose }) => {
    const [days, setDays] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await api.get("/attendance/messages", {
                    params: { user_id: record.user_id, month: record.month, field },
                });
                if (!cancel) setDays(r.data?.days || []);
            } catch (e) {
                if (!cancel) setErr(errMsg(e, "Failed to load messages"));
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [record.user_id, record.month, field]);

    const isIn = field.startsWith("late_");
    return createPortal(
        <div className="fixed inset-0 z-[220] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Attribution · {record.employee_name}</div>
                        <div className="font-heading font-semibold text-slate-900 text-sm truncate">
                            {labelForField(field, record)} · {record.month}
                        </div>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 inline-flex items-center justify-center" aria-label="Close">
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-4 overflow-y-auto flex-1 space-y-3">
                    {loading && (
                        <div className="flex items-center justify-center gap-2 py-8 text-slate-500 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading message trail…
                        </div>
                    )}
                    {err && <div className="text-rose-600 text-sm">{err}</div>}
                    {!loading && !err && days && days.length === 0 && (
                        <div className="text-slate-500 text-sm text-center py-8">
                            No archived messages for this bucket. This cell was likely edited manually or uploaded via the classic XLSX flow.
                        </div>
                    )}
                    {days && days.map((d, i) => {
                        const cands = isIn ? (d.in_candidates || []) : (d.out_candidates || []);
                        const final = isIn ? d.final_in : d.final_out;
                        return (
                            <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
                                <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-700 border-b border-slate-100 flex items-center justify-between">
                                    <span>{d.date}</span>
                                    <span className="text-[10px] text-slate-500">{cands.length} candidate{cands.length === 1 ? "" : "s"}</span>
                                </div>
                                <ul className="divide-y divide-slate-100">
                                    {cands.map((c, j) => {
                                        const isFinal = final && c.msg === final.msg && c.ts === final.ts;
                                        return (
                                            <li key={j} className={`px-3 py-2 text-xs ${isFinal ? "bg-emerald-50/60" : ""}`}>
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="text-slate-500 tabular-nums">{new Date(c.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                                                    <span className={`px-1.5 h-4 rounded-full text-[9px] font-bold uppercase tracking-wider ${c.kind === "actual" ? "bg-[#2EB4AC]/10 text-[#25908A]" : c.kind === "estimate" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                                                        {c.kind}
                                                    </span>
                                                    {c.kind === "estimate" && Number.isFinite(c.mins) && (
                                                        <span className="text-[10px] font-semibold text-slate-600 tabular-nums" data-testid="drill-counted-time">→ counted as {String(Math.floor(c.mins / 60)).padStart(2, "0")}:{String(c.mins % 60).padStart(2, "0")}</span>
                                                    )}
                                                    {isFinal && <span className="text-[10px] font-bold text-emerald-700">↳ chosen</span>}
                                                </div>
                                                <div className="text-slate-700 leading-snug">{c.msg}</div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
                <div className="px-5 py-2 border-t border-slate-100 bg-slate-50/60 text-[10px] text-slate-500">
                    Source: <span className="font-semibold text-slate-700">Chat classifier</span> — winning message shown with a green tint.
                </div>
            </div>
        </div>,
        document.body,
    );
};



// ---------- Add attendance row dialog (super admin) ----------
// Lets super admins manually create a record for any user / month — useful
// when the bulk upload missed someone (e.g. themselves, or a new joiner).
// One row per (user, month); 409 surfaces as a friendly toast.
export const AddAttendanceDialog = ({ visibleUsers, onClose, onCreated }) => {
    const today = new Date();
    const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const [form, setForm] = useState({
        user_id: "",
        month: defaultMonth,
        shift: "corporate",
        late_slab_1: 0,
        late_slab_2: 0,
        late_slab_3: 0,
        early_slab_1: 0,
        early_slab_2: 0,
        early_slab_3: 0,
        oof: 0,
        wfh: 0,
    });
    const [saving, setSaving] = useState(false);
    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const total = ["late_slab_1", "late_slab_2", "late_slab_3",
                   "early_slab_1", "early_slab_2", "early_slab_3"]
                    .reduce((a, k) => a + (Number(form[k]) || 0), 0);
    // Auto-set the shift default from the selected user (if they have one).
    useEffect(() => {
        if (!form.user_id) return;
        const u = (visibleUsers || []).find((x) => x.user_id === form.user_id);
        if (u?.shift && u.shift !== form.shift) update("shift", u.shift);
    }, [form.user_id]);

    const meta = SHIFT_META[form.shift] || SHIFT_META.corporate;

    const submit = async () => {
        if (!form.user_id) { toast.error("Pick a team member"); return; }
        setSaving(true);
        try {
            // Send BOTH slab-agnostic fields AND the legacy corporate fields
            // (only when shift is corporate). Backend accepts both families;
            // this keeps existing legacy readers correct without double
            // counting.
            const isCorp = form.shift === "corporate";
            const payload = {
                user_id: form.user_id,
                month: form.month,
                shift: form.shift,
                late_slab_1: Number(form.late_slab_1) || 0,
                late_slab_2: Number(form.late_slab_2) || 0,
                late_slab_3: Number(form.late_slab_3) || 0,
                early_slab_1: Number(form.early_slab_1) || 0,
                early_slab_2: Number(form.early_slab_2) || 0,
                early_slab_3: Number(form.early_slab_3) || 0,
                late_1000_1015: isCorp ? (Number(form.late_slab_1) || 0) : 0,
                late_1015_1030: isCorp ? (Number(form.late_slab_2) || 0) : 0,
                late_after_1030: isCorp ? (Number(form.late_slab_3) || 0) : 0,
                early_1800_1830: isCorp ? (Number(form.early_slab_1) || 0) : 0,
                early_1830_1900: isCorp ? (Number(form.early_slab_2) || 0) : 0,
                early_before_1800: isCorp ? (Number(form.early_slab_3) || 0) : 0,
                oof: Number(form.oof) || 0,
                wfh: Number(form.wfh) || 0,
            };
            await api.post("/attendance", payload);
            // Reset saving BEFORE onCreated fires — the parent will unmount
            // this dialog on the same tick and a lingering setState in a
            // `finally` block was previously logging a React warning.
            setSaving(false);
            try {
                onCreated?.();
            } catch (cbErr) {
                // Never let a parent onCreated crash bubble up and leave the
                // page in a broken state.
                // eslint-disable-next-line no-console
                console.error("AddAttendance onCreated error:", cbErr);
                toast.error("Row saved, but the list couldn't refresh — please reload.");
            }
        } catch (e) {
            setSaving(false);
            toast.error(errMsg(e, "Could not add row"));
        }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl max-h-[90vh] overflow-auto">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
                    <div>
                        <h3 className="font-heading font-semibold text-slate-900 text-lg">Add attendance row</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Manual entry for a user + month. Total auto-computes from late/early buckets.</p>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-xs font-semibold text-slate-600 mb-1">Team member</div>
                            <select
                                value={form.user_id}
                                onChange={(e) => update("user_id", e.target.value)}
                                data-testid="add-att-user"
                                className="w-full h-10 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/30 outline-none"
                            >
                                <option value="">— Select —</option>
                                {visibleUsers.filter((u) => u.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((u) => (
                                    <option key={u.user_id} value={u.user_id}>{u.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <div className="text-xs font-semibold text-slate-600 mb-1">Month</div>
                            <input
                                type="month"
                                value={form.month}
                                onChange={(e) => update("month", e.target.value)}
                                data-testid="add-att-month"
                                className="w-full h-10 px-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/30 outline-none"
                            />
                        </label>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
                            <span>Late entries · <span className="text-slate-400 normal-case font-medium tracking-normal">{meta.window}</span></span>
                            <select
                                value={form.shift}
                                onChange={(e) => update("shift", e.target.value)}
                                data-testid="add-att-shift"
                                className="h-7 px-2 border border-slate-200 rounded-md text-[11px] bg-white text-slate-700"
                            >
                                <option value="corporate">Corporate (10-7)</option>
                                <option value="firmware">Firmware (9-6)</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <NumberField label={meta.lateLabels[0]} value={form.late_slab_1} onChange={(v) => update("late_slab_1", v)} testid="add-late-1" />
                            <NumberField label={meta.lateLabels[1]} value={form.late_slab_2} onChange={(v) => update("late_slab_2", v)} testid="add-late-2" />
                            <NumberField label={meta.lateLabels[2]} value={form.late_slab_3} onChange={(v) => update("late_slab_3", v)} tone="warning" testid="add-late-3" />
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Early exits</div>
                        <div className="grid grid-cols-3 gap-2">
                            <NumberField label={meta.earlyLabels[0]} value={form.early_slab_1} onChange={(v) => update("early_slab_1", v)} testid="add-early-1" />
                            <NumberField label={meta.earlyLabels[1]} value={form.early_slab_2} onChange={(v) => update("early_slab_2", v)} testid="add-early-2" />
                            <NumberField label={meta.earlyLabels[2]} value={form.early_slab_3} onChange={(v) => update("early_slab_3", v)} tone="danger" testid="add-early-3" />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <NumberField label="OOF days" value={form.oof} onChange={(v) => update("oof", v)} testid="add-oof" allowDecimal />
                        <NumberField label="WFH days" value={form.wfh} onChange={(v) => update("wfh", v)} testid="add-wfh" allowDecimal />
                        <div className="block">
                            <div className="text-xs font-semibold text-slate-600 mb-1">Total (auto)</div>
                            <div className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-slate-50 flex items-center font-bold tabular-nums text-slate-900">
                                {total}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
                    <button
                        onClick={submit}
                        disabled={saving || !form.user_id}
                        data-testid="add-att-submit"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-medium text-sm disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Add row
                    </button>
                </div>
            </div>
        </div>
    );
};

const NumberField = ({ label, value, onChange, tone, testid, allowDecimal = false }) => {
    // For decimal-capable fields (OOF / WFH half-days) we keep a local
    // draft string so users can type transient states like "0." or ".5"
    // without React overwriting the input mid-typing. Integer fields keep
    // the simpler controlled-number behaviour.
    const [draft, setDraft] = useState(String(value ?? 0));
    useEffect(() => {
        // Reconcile local draft when the parent value changes from outside
        // (e.g. shift switch resets, external prefill). Only overwrite when
        // the numeric interpretation differs so we don't stomp on
        // in-progress typing like "0.".
        if (Number(draft) !== Number(value ?? 0)) {
            setDraft(String(value ?? 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (
        <label className="block">
            <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
            <input
                type="number"
                min={0}
                max={999}
                step={allowDecimal ? "0.5" : "1"}
                value={allowDecimal ? draft : value}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (allowDecimal) {
                        setDraft(raw);
                        if (raw === "" || raw === "." || raw === "-") {
                            onChange(0);
                            return;
                        }
                        const n = Number(raw);
                        onChange(Number.isFinite(n) ? n : 0);
                    } else {
                        onChange(raw === "" ? 0 : Number(raw));
                    }
                }}
                onBlur={() => {
                    if (!allowDecimal) return;
                    // Normalise the draft on blur so a stray "0." becomes "0".
                    const n = Number(draft);
                    setDraft(Number.isFinite(n) ? String(n) : "0");
                }}
                data-testid={testid}
                className={`w-full h-10 px-2 border rounded-lg text-sm text-center tabular-nums focus:ring-2 focus:ring-[#2EB4AC]/30 outline-none ${
                    tone === "danger" ? "border-red-200 text-red-700" : tone === "warning" ? "border-yellow-200 text-yellow-700" : "border-slate-200 text-slate-900"
                } focus:border-[#2EB4AC]`}
            />
        </label>
    );
};



export default Attendance;
